import { t } from "@lingui/core/macro"
import { useMutation } from "@tanstack/react-query"
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router"
import { ArrowLeft, Save } from "lucide-react"
import { useState } from "react"
import { toast } from "sonner"
import type { ValueType } from "@/features/feature-flags/types/flagTypes"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Switch } from "@/components/ui/switch"
import { Textarea } from "@/components/ui/textarea"
import { createFlagFn } from "@/features/feature-flags/server/flagManagement"

export const Route = createFileRoute("/(authenticated)/settings/feature-flags/new")({
  component: NewFlagPage,
})

function NewFlagPage() {
  const navigate = useNavigate()

  const [key, setKey] = useState("")
  const [name, setName] = useState("")
  const [description, setDescription] = useState("")
  const [valueType, setValueType] = useState<ValueType>("boolean")
  const [defaultValue, setDefaultValue] = useState("false")

  const createMutation = useMutation({
    mutationFn: createFlagFn,
    onSuccess: (result) => {
      toast.success(t`Flag "${result.key}" created`)
      void navigate({
        to: "/settings/feature-flags/$flagId",
        params: { flagId: result.id },
      })
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : t`Failed to create flag`)
    },
  })

  const handleKeyChange = (value: string) => {
    // Auto-format key to kebab-case
    const formatted = value
      .toLowerCase()
      .replace(/[^a-z0-9-]/g, "-")
      .replace(/-+/g, "-")
    setKey(formatted)
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    // Parse default value based on type
    let parsedDefaultValue: boolean | string | number = defaultValue
    if (valueType === "boolean") {
      parsedDefaultValue = defaultValue.toLowerCase() === "true"
    } else if (valueType === "number") {
      parsedDefaultValue = parseFloat(defaultValue) || 0
    }

    createMutation.mutate({
      data: {
        key,
        name,
        description: description || undefined,
        valueType,
        defaultValue: parsedDefaultValue,
      },
    })
  }

  const isValid = key.length >= 1 && name.length >= 1

  return (
    <div className="flex-1 space-y-6 overflow-y-auto pb-8">
      <div>
        <Link
          to="/settings/feature-flags"
          className="mb-4 inline-flex items-center text-sm text-blue-600 hover:text-blue-800 hover:underline"
        >
          <ArrowLeft className="mr-1 h-4 w-4" />
          {t`Back to Feature Flags`}
        </Link>
        <div className="mb-2 text-xs uppercase tracking-wider text-neutral-500 dark:text-neutral-300">
          {t`Feature Flag`}
        </div>
        <h1 className="mb-2 font-serif text-4xl font-bold text-neutral-900 dark:text-white">
          {t`Create New Flag`}
        </h1>
        <p className="text-neutral-500 dark:text-neutral-300">
          {t`Create a new feature flag for controlled rollouts`}
        </p>
      </div>

      <form onSubmit={handleSubmit}>
        <Card>
          <CardHeader>
            <CardTitle>{t`Flag Details`}</CardTitle>
            <CardDescription>{t`Define the basic properties of your feature flag`}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid gap-6 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="key">{t`Key`}</Label>
                <Input
                  id="key"
                  placeholder="my-feature-flag"
                  value={key}
                  onChange={(e) => handleKeyChange(e.target.value)}
                  className="font-mono"
                />
                <p className="text-sm text-neutral-500">
                  {t`Unique identifier used in code (kebab-case)`}
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="name">{t`Name`}</Label>
                <Input
                  id="name"
                  placeholder="My Feature Flag"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
                <p className="text-sm text-neutral-500">{t`Human-readable name for the flag`}</p>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">{t`Description`}</Label>
              <Textarea
                id="description"
                placeholder="Describe what this flag controls..."
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
              />
            </div>

            <div className="grid gap-6 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="valueType">{t`Value Type`}</Label>
                <Select value={valueType} onValueChange={(v) => setValueType(v as ValueType)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="boolean">{t`Boolean`}</SelectItem>
                    <SelectItem value="string">{t`String`}</SelectItem>
                    <SelectItem value="number">{t`Number`}</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-sm text-neutral-500">{t`The type of value this flag returns`}</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="defaultValue">{t`Default Value`}</Label>
                {valueType === "boolean" ? (
                  <div className="flex items-center gap-4 pt-2">
                    <Switch
                      checked={defaultValue.toLowerCase() === "true"}
                      onCheckedChange={(v) => setDefaultValue(String(v))}
                    />
                    <span className="text-sm text-neutral-500">
                      {defaultValue.toLowerCase() === "true" ? "true" : "false"}
                    </span>
                  </div>
                ) : (
                  <Input
                    id="defaultValue"
                    type={valueType === "number" ? "number" : "text"}
                    value={defaultValue}
                    onChange={(e) => setDefaultValue(e.target.value)}
                  />
                )}
              </div>
            </div>

            <div className="flex justify-end gap-4 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => void navigate({ to: "/settings/feature-flags" })}
              >
                {t`Cancel`}
              </Button>
              <Button type="submit" disabled={!isValid || createMutation.isPending}>
                <Save className="mr-2 size-4" />
                {createMutation.isPending ? t`Creating...` : t`Create Flag`}
              </Button>
            </div>
          </CardContent>
        </Card>
      </form>
    </div>
  )
}
